// src/app/pages/admin/AdminTradeHistoryPage.jsx
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAdminTradeHistory } from "../../features/trade/tradeApi.js";

function fmtNum(x, d = 2) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "-";
  return n.toFixed(d);
}

function fmtDate(x) {
  if (!x) return "-";
  const t = new Date(x);
  if (!Number.isFinite(t.getTime())) return "-";
  return t.toLocaleString();
}

function statusLabel(tr) {
  const outcome = String(tr?.outcome || "").toUpperCase();
  if (outcome === "WIN") return { text: "Won", cls: "text-green-400" };
  if (outcome === "LOSE") return { text: "Lost", cls: "text-red-400" };
  if (outcome === "PUSH") return { text: "Push", cls: "text-yellow-400" };

  const st = String(tr?.status || "").toUpperCase();
  if (st === "APPROVED") return { text: "Approved", cls: "text-slate-200" };
  if (st === "PENDING") return { text: "Pending", cls: "text-yellow-300" };
  if (st === "REJECTED") return { text: "Rejected", cls: "text-red-300" };
  if (st === "COMPLETED") return { text: "Completed", cls: "text-green-300" };
  return { text: st || "-", cls: "text-slate-300" };
}

function dirLabel(tr) {
  const d = String(tr?.direction || "").toUpperCase();
  if (d === "DOWN" || d === "SHORT" || d === "SELL") return { text: "Short", cls: "text-red-400" };
  if (d === "UP" || d === "LONG" || d === "BUY") return { text: "Long", cls: "text-green-400" };
  return { text: d || "-", cls: "text-slate-200" };
}

function TradeCard({ tr }) {
  const dir = dirLabel(tr);
  const st = statusLabel(tr);

  const amount = fmtNum(tr?.stake_amount, 8);
  const asset = String(tr?.funded_asset || "USDT").toUpperCase();
  const pct = tr?.payout_multiplier ? `${((tr.payout_multiplier - 1) * 100).toFixed(0)}%` : "-";

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
      <div className="text-xs text-slate-400 mb-3">
        Trade ID: <span className="text-slate-300">{String(tr?.id).slice(0, 8)}...</span> ·{" "}
        User: <span className="text-slate-300">{String(tr?.user_id).slice(0, 8)}...</span> ·{" "}
        Market: <span className="text-slate-300">{tr?.market}</span>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div className="text-slate-300">Amount:</div>
        <div className="text-right text-slate-100">
          {amount} {asset}
        </div>

        <div className="text-slate-300">Percentage:</div>
        <div className="text-right text-slate-100">{pct}</div>

        <div className="text-slate-300">Purchase Price:</div>
        <div className="text-right text-slate-100">${fmtNum(tr?.entry_price, 2)}</div>

        <div className="text-slate-300">Contract:</div>
        <div className="text-right text-slate-100">
          {Number.isFinite(Number(tr?.duration_seconds)) ? `${tr.duration_seconds}s` : "-"}
        </div>

        <div className="text-slate-300">Start Time:</div>
        <div className="text-right text-slate-100">{fmtDate(tr?.approved_at || tr?.created_at)}</div>

        <div className="text-slate-300">Delivery Time:</div>
        <div className="text-right text-slate-100">{fmtDate(tr?.resolved_at || tr?.resolve_at)}</div>

        <div className="text-slate-300">Direction:</div>
        <div className={`text-right font-semibold ${dir.cls}`}>{dir.text}</div>

        <div className="text-slate-300">Delivery Price:</div>
        <div className="text-right text-slate-100">${fmtNum(tr?.exit_price, 2)}</div>

        <div className="text-slate-300">Status:</div>
        <div className={`text-right font-semibold ${st.cls}`}>{st.text}</div>
      </div>
    </div>
  );
}

export default function AdminTradeHistoryPage() {
  const [tab, setTab] = useState("PENDING");
  const [page, setPage] = useState(0);

  const limit = 50;
  const offset = page * limit;

  const params = useMemo(() => {
    if (tab === "COMPLETED") {
      return { settled_only: true, limit, offset };
    }
    // “Pending” here means: approved/running trades not settled yet
    return { settled_only: false, status: "APPROVED", limit, offset };
  }, [tab, limit, offset]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["adminTradeHistory", tab, limit, offset],
    queryFn: () => getAdminTradeHistory(params),
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Admin Trade History</h1>
        <button
          onClick={() => refetch()}
          className="px-3 py-2 rounded-lg bg-slate-900 border border-white/10 text-slate-200 text-sm hover:bg-slate-800"
          type="button"
        >
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex rounded-xl bg-slate-900 border border-white/10 p-1 w-fit">
        <button
          className={`px-6 py-2 rounded-lg ${tab === "PENDING" ? "bg-slate-800 text-white" : "text-slate-300"}`}
          onClick={() => {
            setTab("PENDING");
            setPage(0);
          }}
          type="button"
        >
          Running (Approved)
        </button>
        <button
          className={`px-6 py-2 rounded-lg ${tab === "COMPLETED" ? "bg-green-500 text-black" : "text-slate-300"}`}
          onClick={() => {
            setTab("COMPLETED");
            setPage(0);
          }}
          type="button"
        >
          Completed
        </button>
      </div>

      {isLoading && <div className="text-slate-300">Loading trades...</div>}
      {error && (
        <div className="text-red-400">
          Error: {error?.response?.data?.detail || error?.message || "Request failed"}
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className="text-slate-400 text-sm rounded-xl border border-white/10 bg-slate-900 p-4">
          No trades found for this tab.
        </div>
      )}

      <div className="space-y-3">{items.map((t) => <TradeCard key={t.id} tr={t} />)}</div>

      <div className="flex justify-between text-slate-400 text-sm">
        <div>
          Showing {items.length} of {total}
        </div>
        <div className="space-x-2">
          <button
            className="px-3 py-1 rounded bg-slate-900 border border-white/10 disabled:opacity-40"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            type="button"
          >
            Prev
          </button>
          <button
            className="px-3 py-1 rounded bg-slate-900 border border-white/10 disabled:opacity-40"
            disabled={offset + limit >= total}
            onClick={() => setPage(page + 1)}
            type="button"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
